/* eslint-disable react-hooks/exhaustive-deps */
import DocumentViewerModal from "components/DocumentViewer";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import {
  editPrivateProsecution,
  fetchPrivateProsecutionById,
} from "services/privateProsecutionService";
import {
  FILE_URL,
  PrivateProsecution,
  PrivateProsecutionForwardingStatus,
  PrivateProsecutionStatus,
  Roles,
} from "types";
import { errorHandlerOnForm, hasRole } from "utils/helper";
import PrivateProsecutionFeedbackSection from "./fragment/PrivateProsecutionFeedback";
import RegistrarForm from "./fragment/RegistrarForm";
import RequestPrivateProsecutiion from "./fragment/ReqeustPrivateProsecution";
import RespondBackFeedback from "./fragment/RespondBackFeedback";
import PrivateProsecutionReportSection from "./fragment/PrivateProsecutionReport";
import AddPrivateProsecutionReport from "./fragment/AddPrivateProsecutionReport";

type Props = {
  selectedId: string | number;
};

const PrivateProsecutionDetail: React.FC<Props> = ({ selectedId }) => {
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const [privateProsecution, setPrivateProsecution] =
    useState<PrivateProsecution | null>(null);

  const [openRegistrarForm, setOpenRegistrarForm] = useState(false);
  const [openReport, setOpenReport] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openAddFeedack, setOpenAddFeedack] = useState(false);
  const [openFeedbackSection, setOpenFeedbackSection] = useState(false);
  const [openReportSection, setOpenReportSection] = useState(false);

  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isRegistrar = hasRole(loginData?.roles, [Roles.registrar]);
  const isCoordinator = hasRole(loginData?.roles, [Roles.coordinator]);

  const fetchData = () => {
    if (!selectedId) return;
    fetchPrivateProsecutionById(selectedId)
      .then((data: PrivateProsecution) => {
        setPrivateProsecution(data);
        data?.forwarding_status === "forwarded_to_indvdual"
          ? setOpenReportSection(true)
          : setOpenFeedbackSection(true);
      })
      .catch((error) => {
        showTooltip(
          errorHandlerOnForm(error) || "Error fetching case details",
          "error",
        );
      });
  };

  useEffect(() => {
    fetchData();
  }, [selectedId, showTooltip]);

  const updateStatus = (
    status: keyof typeof PrivateProsecutionStatus,
    forwarding_status?: keyof typeof PrivateProsecutionForwardingStatus,
  ) => {
    if (!selectedId) return;
    editPrivateProsecution({
      id: selectedId,
      status: status,
      forwarding_status,
    })
      .then(() => {
        showTooltip("Successfully Updated!", "success");
        fetchData();
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      });
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <span className="text-2xl">❓</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Private Prosecution Detial
              </h1>
              <p className="text-gray-600 mt-1">
                Review and manage private prosecution.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-4">
          {isProsecutor &&
            (privateProsecution?.status === "pending" ||
              privateProsecution?.status === "rejected") &&
            privateProsecution?.forwarding_status === "on_prosecutor_desk" && (
              <div className="flex flex-row gap-2">
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
                  onClick={() => {
                    setOpenEdit(true);
                  }}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
                  onClick={() => {
                    updateStatus("pending", "forward_to_coordinator");
                  }}
                >
                  Forward to Coordinator
                </button>
              </div>
            )}

          {isCoordinator &&
            privateProsecution?.status === "pending" &&
            privateProsecution?.forwarding_status ===
              "forward_to_coordinator" && (
              <div className="flex flex-row gap-2">
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
                  onClick={() => {
                    updateStatus("approved", "forward_to_registrar");
                  }}
                >
                  Approve
                </button>
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
                  onClick={() => {
                    setOpenAddFeedack(true);
                  }}
                >
                  Respond Back
                </button>
              </div>
            )}

          {isRegistrar &&
            privateProsecution?.status === "approved" &&
            privateProsecution?.forwarding_status ===
              "forward_to_registrar" && (
              <button
                type="button"
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
                onClick={() => {
                  setOpenRegistrarForm(true);
                }}
              >
                Add Registration
              </button>
            )}

          {isProsecutor &&
            privateProsecution?.forwarding_status ===
              "forwarded_to_indvdual" && (
              <button
                type="button"
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
                onClick={() => {
                  setOpenReport(true);
                }}
              >
                Add Report
              </button>
            )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-1 space-y-6">
          {!privateProsecution ? (
            <div className="flex justify-center items-center h-40">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">
                Loading private prosecution details...
              </span>
            </div>
          ) : (
            <>
              {/* Case Information */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Application Name
                    </p>
                    <p className="text-sm font-semibold text-gray-900 mt-1">
                      {privateProsecution?.applicant_name || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Contact</p>
                    <p className="text-sm font-semibold text-gray-900 mt-1">
                      {privateProsecution?.applicant_contact || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Phone Number
                    </p>
                    <p className="text-sm font-semibold text-gray-900 mt-1">
                      {privateProsecution?.applicant_phone_number || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Email</p>
                    <p className="text-sm font-semibold text-gray-900 mt-1">
                      {privateProsecution?.applicant_email || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Region</p>
                    <p className="text-sm font-semibold text-gray-900 mt-1">
                      {privateProsecution?.applicant_region || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">City</p>
                    <p className="text-sm font-semibold text-gray-900 mt-1">
                      {privateProsecution?.applicant_city || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Sub City
                    </p>
                    <p className="text-sm font-semibold text-gray-900 mt-1">
                      {privateProsecution?.applicant_sub_city || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Woreda</p>
                    <p className="text-sm font-semibold text-gray-900 mt-1">
                      {privateProsecution?.applicant_woreda || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Kebele</p>
                    <p className="text-sm font-semibold text-gray-900 mt-1">
                      {privateProsecution?.applicant_kebele || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      House Number
                    </p>
                    <p className="text-sm font-semibold text-gray-900 mt-1">
                      {privateProsecution?.applicant_house_number || "N/A"}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-600">Status</p>
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mt-1 ${
                        privateProsecution.status &&
                        getStatusColor(privateProsecution.status)
                      }`}
                    >
                      {privateProsecution.status
                        ? PrivateProsecutionStatus[privateProsecution.status]
                        : "N/A"}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Forward Status
                    </p>
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mt-1 ${
                        privateProsecution.forwarding_status &&
                        getStatusColor(privateProsecution.forwarding_status)
                      }`}
                    >
                      {privateProsecution.forwarding_status
                        ? PrivateProsecutionForwardingStatus[
                            privateProsecution.forwarding_status
                          ]
                        : "N/A"}
                    </span>
                  </div>
                  {privateProsecution?.registaration_number && (
                    <div>
                      <p className="text-sm font-medium text-gray-600">
                        Reference Number
                      </p>
                      <p className="text-sm font-semibold text-gray-900 mt-1">
                        {privateProsecution?.registaration_number}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Registration Date
                    </p>
                    <p className="text-sm font-semibold text-gray-900 mt-1">
                      {privateProsecution.registration_date
                        ? new Date(
                            privateProsecution.registration_date,
                          ).toLocaleDateString()
                        : "N/A"}
                    </p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-sm font-medium text-gray-600">
                      Reason for Private Pprosecution
                    </p>
                    <p className="text-sm text-gray-700 mt-2 bg-gray-50 p-4 rounded-lg">
                      {privateProsecution.reason_for_private_prosecution ||
                        "N/A"}
                    </p>
                  </div>
                </div>
                <DocumentViewerModal
                  documents={privateProsecution?.documents || ([] as any)}
                  baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.PRIVATE_PROSECUTION}`}
                  layout="list"
                  // label="Letter Upload"
                  hideEmptyFile={true}
                />
              </div>
            </>
          )}
        </div>

        {/* Sidebar */}
        <div className="flex flex-col gap-6">
          {/* Tab buttons */}
          <div
            className="flex flex-row rounded-lg bg-gray-100 p-1"
            role="tablist"
          >
            <button
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                openReportSection
                  ? "bg-[#AA232F] text-white shadow-sm"
                  : "text-gray-700 hover:bg-gray-200"
              }`}
              role="tab"
              aria-selected={openReportSection}
              onClick={() => {
                setOpenReportSection(true);
                setOpenFeedbackSection(false);
              }}
            >
              {/* Report icon */}
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              Report
            </button>
            <button
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                openFeedbackSection
                  ? "bg-[#E90101] text-white shadow-sm"
                  : "text-gray-700 hover:bg-gray-200"
              }`}
              role="tab"
              aria-selected={openFeedbackSection}
              onClick={() => {
                setOpenFeedbackSection(true);
                setOpenReportSection(false);
              }}
            >
              {/* Feedback icon */}
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                />
              </svg>
              Feedback
            </button>
          </div>

          {/* Tab panels */}
          {openFeedbackSection && privateProsecution && (
            <div className="space-y-6 animate-fadeIn" role="tabpanel">
              <PrivateProsecutionFeedbackSection
                privateProsecution={privateProsecution}
                feedbacks={privateProsecution?.feedbacks || []}
                setRefetch={() => fetchData()}
              />
            </div>
          )}
          {openReportSection && (
            <div className="space-y-6 animate-fadeIn" role="tabpanel">
              <PrivateProsecutionReportSection
                reports={privateProsecution?.reports || []}
              />
            </div>
          )}
        </div>
      </div>

      {privateProsecution && openRegistrarForm && (
        <RegistrarForm
          privateProsecution={privateProsecution}
          setRefetch={() => {
            fetchData();
            setOpenRegistrarForm(false);
          }}
          onClose={() => {
            setOpenRegistrarForm(false);
          }}
        />
      )}

      {privateProsecution && openEdit && (
        <RequestPrivateProsecutiion
          privateProsecution={privateProsecution}
          setRefetch={() => {
            fetchData();
            setOpenEdit(false);
          }}
          onCancel={() => {
            setOpenEdit(false);
          }}
        />
      )}

      {privateProsecution && openAddFeedack && (
        <RespondBackFeedback
          privateProsecution={privateProsecution}
          setRefetch={() => {
            fetchData();
            setOpenAddFeedack(false);
          }}
          onCancel={() => {
            setOpenAddFeedack(false);
          }}
        />
      )}

      {privateProsecution && openReport && (
        <AddPrivateProsecutionReport
          privateProsecution={privateProsecution}
          setRefetch={() => {
            fetchData();
            setOpenReport(false);
          }}
          onCancel={() => {
            setOpenReport(false);
          }}
        />
      )}
    </div>
  );
};

// Helper function for status colors
const getStatusColor = (status?: string) => {
  if (!status) return "bg-gray-100 text-gray-800";

  switch (status.toLowerCase()) {
    case "pending":
      return "bg-yellow-100 text-yellow-800";
    case "approved":
      return "bg-green-100 text-green-800";
    case "rejected":
      return "bg-red-100 text-red-800";
    case "forwarded_to_cordinator":
      return "bg-blue-100 text-blue-800";
    case "assigned_to_team_asking":
      return "bg-indigo-100 text-indigo-800";
    case "forwarded_to_registrar":
      return "bg-purple-100 text-purple-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

export default PrivateProsecutionDetail;
